import Button from '@/common/components/Button'
import MaskedTextField from '@/common/components/MaskedTextField'
import SelectController from '@/common/components/SelectController'
import TextArea from '@/common/components/TextArea'
import TextField from '@/common/components/TextField'
import { useGeosOptions } from '@/common/hooks/queries/useGeosOptions'
import useDebounce from '@/common/hooks/useDebounce'
import { GeoTransformers } from '@/common/services/geoTransformers'
import { dateIsoTransformer } from '@/common/utils/dateTransformer'
import { RequestedReview } from '@/features/establishmentLeadsFunnel/components/RequestedReview'
import { ReviewButtons } from '@/features/establishmentLeadsFunnel/components/ReviewButtons'
import { ContractFileUploader } from '@/features/publicCompanyLeadsFunnel/components/ContractFileUploader'
import { options as governmentSegmentOptions } from '@/features/publicCompanyLeadsFunnel/constants/governmentSegmentOptions.constant'
import { useUpdatePublicCompanyLeadContractFile } from '@/features/publicCompanyLeadsFunnel/hooks/useUpdatePublicCompanyLeadContractFile'
import { PublicCompanyLeadsFunnelUpdateValidationSchema } from '@/features/publicCompanyLeadsFunnel/schemas/update.schema'
import {
  BiddingInformationData,
  ContractData,
  PublicCompanyLeadData,
} from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { filterOptions } from '@/features/users/constants/filterOptions.constant'
import { UseMutateFunction, useQueryClient } from '@tanstack/react-query'
import { CalendarDaysIcon } from 'lucide-react'
import { FilterOptionOption } from 'node_modules/react-select/dist/declarations/src/filters'
import { BaseSyntheticEvent, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

interface BiddingInformationSectionProps {
  publicCompanyLeadId: string
  publicCompanyLeadState: PublicCompanyLeadData['state']
  contractId?: string
  contractFile?: ContractData['file']
  bidding_information?: BiddingInformationData
  submittableOnBlur?: (
    e: React.FocusEvent<HTMLInputElement, Element>,
    onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void,
  ) => void
  submit?: (e?: BaseSyntheticEvent) => Promise<void>
  approveBiddingInformationLead?: UseMutateFunction<void, Error, { description?: string } | undefined, unknown>
  declineBiddingInformationLead?: UseMutateFunction<void, Error, { description?: string } | undefined, unknown>
  disabled?: boolean
}

export const BiddingInformationSection = ({
  publicCompanyLeadId,
  publicCompanyLeadState,
  contractId,
  contractFile,
  bidding_information,
  submittableOnBlur,
  submit,
  approveBiddingInformationLead,
  declineBiddingInformationLead,
  disabled,
}: BiddingInformationSectionProps) => {
  const [serverError, setServerError] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const isInBidding = publicCompanyLeadState === 'in_bidding'
  const isInContracting = publicCompanyLeadState === 'contracting'
  const isInContractingManagementReview = publicCompanyLeadState === 'contracting_management_review'
  const isInContractingFinalization = publicCompanyLeadState === 'contracting_finalization'
  const isInCompleted = publicCompanyLeadState === 'completed'

  const [geoSearch, setGeoSearch] = useDebounce('')

  const geos = useGeosOptions(geoSearch)

  const {
    control,
    formState: { errors },
  } = useFormContext<PublicCompanyLeadsFunnelUpdateValidationSchema>()

  const onSuccess = () => {
    setServerError(false)
    queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', publicCompanyLeadId] })
  }

  const onError = () => setServerError(true)

  const { mutate: uploadContractFile } = useUpdatePublicCompanyLeadContractFile({
    publicCompanyLeadId,
    onSuccess,
    onError,
  })

  return (
    <div className="flex flex-col gap-4">
      {serverError && (
        <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>
      )}
      <div className="flex justify-between">
        <div className="flex w-full items-center justify-between">
          <p className="text-[18px] font-bold">Detalhes da licitação</p>
          <Button
            type="button"
            name="public_company_contract_details"
            className={publicCompanyLeadState === 'completed' ? '' : 'bg-success-disabled hover:bg-success-hover'}
            operationKlassToCheckPermission="Admin::Funnel::Public::Leads::Show"
            onClick={() => navigate(`/public-clients/${publicCompanyLeadId}/contracts/${contractId}/edit`)}
          >
            Ver condição comercial
          </Button>
        </div>
      </div>
      {bidding_information?.last_review?.state === 'disapproved' && (
        <RequestedReview description={bidding_information?.last_review?.description} />
      )}
      <div className="flex justify-between items-center">
        {(isInContracting || isInContractingManagementReview || isInContractingFinalization || isInCompleted) && (
          <ContractFileUploader
            contractFile={contractFile}
            uploadContractFile={uploadContractFile}
            disabled={!isInContracting}
          />
        )}
        {approveBiddingInformationLead && declineBiddingInformationLead && (
          <div className="ml-auto">
            <ReviewButtons
              reviewState={bidding_information?.last_review?.state}
              onApprove={approveBiddingInformationLead}
              onDecline={declineBiddingInformationLead}
            />
          </div>
        )}
      </div>
      <div className="flex w-full gap-x-2">
        <Controller
          control={control}
          name="bidding_information.notice_number"
          defaultValue={bidding_information?.notice_number}
          disabled={isInBidding || disabled}
          render={({ field: { onBlur, ...rest } }) => (
            <TextField
              label="Edital"
              markAsRequired
              placeholder="Digite o número do edital"
              maxLength={64}
              className="w-1/2"
              {...rest}
              errorMessage={errors?.bidding_information?.notice_number?.message}
              onBlur={(e) => submittableOnBlur?.(e, onBlur)}
            />
          )}
        />
        <Controller
          control={control}
          name="bidding_information.contest_date"
          defaultValue={bidding_information?.contest_date ? dateIsoTransformer(bidding_information.contest_date) : ''}
          disabled={disabled}
          render={({ field: { onBlur, ...rest } }) => (
            <MaskedTextField
              label="Data prevista para licitação"
              markAsRequired
              mask="00/00/0000"
              placeholder="Selecione a data"
              className="w-1/2"
              LeftIcon={<CalendarDaysIcon strokeWidth={1} />}
              {...rest}
              errorMessage={errors?.bidding_information?.contest_date?.message}
              onBlur={(e) => submittableOnBlur?.(e, onBlur)}
            />
          )}
        />
      </div>
      <Controller
        control={control}
        name="bidding_information.object_of_the_bidding"
        defaultValue={bidding_information?.object_of_the_bidding}
        disabled={isInBidding || disabled}
        render={({ field: { onBlur, ...rest } }) => (
          <TextArea
            label="Objeto da licitação"
            markAsRequired
            placeholder="Digite aqui o objeto da licitação"
            maxLength={1024}
            {...rest}
            errorMessage={errors?.bidding_information?.object_of_the_bidding?.message}
            onBlur={(e) => submittableOnBlur?.(e as unknown as React.FocusEvent<HTMLInputElement, Element>, onBlur)}
          />
        )}
      />
      <SelectController
        label="Segmento de governo"
        markAsRequired
        placeholder="Selecione o segmento"
        control={control}
        name="bidding_information.government_segment"
        defaultValue={governmentSegmentOptions.find(({ value }) => value === bidding_information?.government_segment)}
        errorMessage={errors?.bidding_information?.government_segment?.message}
        options={governmentSegmentOptions}
        triggerOnChange={submit}
        isDisabled={isInBidding || disabled}
      />
      <div className="flex items-start gap-x-2">
        <Controller
          control={control}
          name="bidding_information.estimated_equipment_total"
          defaultValue={
            bidding_information?.estimated_equipment_total ? `${bidding_information.estimated_equipment_total}` : ''
          }
          disabled={isInBidding || disabled}
          render={({ field: { onBlur, ...rest } }) => (
            <MaskedTextField
              label="Quantidade total estimada de Equipamentos"
              mask="0000"
              className="w-1/2"
              {...rest}
              errorMessage={errors?.bidding_information?.estimated_equipment_total?.message}
              onBlur={(e) => submittableOnBlur?.(e, onBlur)}
            />
          )}
        />
        <SelectController
          label="Região de atuação esperada"
          markAsRequired
          control={control}
          name="bidding_information.geo_geoables"
          className="w-1/2"
          triggerOnChange={submit}
          isMulti
          onInputChange={setGeoSearch}
          defaultValue={GeoTransformers.mapToGeo()(bidding_information?.geo_geoables)}
          filterOption={
            filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean
          }
          options={GeoTransformers.mapToGeoOptions()(geos)}
          isDisabled={isInBidding || disabled}
        />
      </div>
      <Controller
        control={control}
        name="bidding_information.minimum_establishments_in_region"
        defaultValue={
          bidding_information?.minimum_establishments_in_region
            ? `${bidding_information.minimum_establishments_in_region}`
            : ''
        }
        disabled={isInBidding || disabled}
        render={({ field: { onBlur, ...rest } }) => (
          <MaskedTextField
            label="Quantidade mínima de credenciados"
            mask="0000"
            {...rest}
            errorMessage={errors?.bidding_information?.minimum_establishments_in_region?.message}
            onBlur={(e) => submittableOnBlur?.(e, onBlur)}
          />
        )}
      />
    </div>
  )
}
